import axios, { AxiosHeaders } from 'axios';
import {
  clearAuthCookie,
  getAuthFromCookie,
  redirectToCognitoLogin,
  setAuthCookie,
} from '../utils/cognitoAuth';

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 10000,
});

const cognitoClient = axios.create({
  baseURL: import.meta.env.VITE_COGNITO_URL,
  timeout: 30000,
});

const cognitoRequestConfig = {
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
  },
};

/**
 * Get access token from Cognito API
 *
 * @async
 * @return {Promise} Response from API under Axios instance
 */
export const getAccessToken = async (code) => {
  const redirectURI = window.encodeURI(`https://${window.location.hostname}/login`);

  const result = await cognitoClient.post('/oauth2/token', {
    grant_type: 'authorization_code',
    code,
    client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
    redirect_uri: redirectURI,
  }, cognitoRequestConfig);

  return result?.data;
};

/**
 * Refresh token from Cognito API
 *
 * @async
 * @return {Promise} Response from API under Axios instance
 */
export const getRefreshToken = async (refreshToken) => {
  const result = await cognitoClient.post('/oauth2/token', {
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
  }, cognitoRequestConfig);

  return result?.data;
};

/**
 * Axios interceptor to pass the JWT token if exists
 *
 * @param {import('axios').InternalAxiosRequestConfig} axiosConfig
 * @returns {import('axios').InternalAxiosRequestConfig}
 */
const onRequest = (axiosConfig) => {
  const auth = getAuthFromCookie();
  const newConfig = { ...axiosConfig };

  if (auth?.access_token) {
    newConfig.headers = new AxiosHeaders({
      ...axiosConfig.headers,
      Authorization: `Bearer ${auth.access_token}`,
    });
  }

  return newConfig;
};

function handleAuthenticationFailure() {
  if (window.location.pathname === '/login') {
    return;
  }

  clearAuthCookie();
  redirectToCognitoLogin();
}

/**
 * API Gateway répond 401 sans en-tête CORS quand il rejette le JWT : le navigateur
 * masque alors le statut et axios ne remonte qu'une erreur réseau.
 *
 * @param {import('axios').AxiosError} error
 * @returns {boolean}
 */
function isAuthenticationError({ response, config, code }) {
  if (response?.status === 401) {
    return true;
  }

  return config?.baseURL === import.meta.env.VITE_RESOURCES_URL && code === 'ERR_NETWORK';
}

/**
 * Axios interceptor to manage API response
 *
 * @param {Object} error
 * @returns {Promise<void>}
 */
const onRejected = async (error) => {
  const { config } = error;

  if (!isAuthenticationError(error) || config?.sent) {
    return Promise.reject(error);
  }

  config.sent = true;

  const auth = getAuthFromCookie();

  if (!auth?.refresh_token) {
    handleAuthenticationFailure();

    return Promise.reject(error);
  }

  try {
    const refreshToken = await getRefreshToken(auth.refresh_token);

    if (!refreshToken?.access_token) {
      handleAuthenticationFailure();

      return Promise.reject(error);
    }

    setAuthCookie({
      access_token: refreshToken.access_token,
      refresh_token: auth.refresh_token,
    });

    const newConfig = { ...config };
    newConfig.headers = new AxiosHeaders({
      ...config.headers,
      Authorization: `Bearer ${refreshToken.access_token}`,
    });

    return instance(newConfig);
  } catch {
    handleAuthenticationFailure();

    return Promise.reject(error);
  }
};

instance.interceptors.request.use(onRequest);
instance.interceptors.response.use((res) => res, onRejected);

export default instance;
